/* eslint-disable react/no-multi-comp */
import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Modal, DatePicker, Input, Button, message } from 'antd';
import { CloseOutlined, ClockCircleOutlined, InfoCircleOutlined } from '@ant-design/icons';
import styled from 'styled-components';
import UploadWithPreviewMultiple from 'components/common/UploadWithPreviewMultiple';
import { useEndTripModal } from './useEndTripModal';

/**
 * End Trip modal (V2) — Standard / Per Order tabs.
 *
 */

const TABS = { STANDARD: 'standard', PER_ORDER: 'per_order' };
const STANDARD_CHARGE_ROWS = [
	{ label: 'Halt Charges', vendorKey: 'haltVendor', clientKey: 'haltClient' },
	{ label: 'Miscellaneous Charges', vendorKey: 'miscVendor', clientKey: 'miscClient' },
	{ label: 'Toll Charges', vendorKey: 'tollVendor', clientKey: 'tollClient' },
	{ label: 'Unloading Charges', vendorKey: 'unloadVendor', clientKey: 'unloadClient' },
	{ label: 'Extra KM', vendorKey: 'extraKmVendor', clientKey: 'extraKmClient' },
	{ label: 'Extra HR', vendorKey: 'extraHrVendor', clientKey: 'extraHrClient' },
	{ label: 'Labour Charges', vendorKey: 'labourVendor', clientKey: 'labourClient' },
	{ label: 'Deduction Amount', vendorKey: 'deductionVendor', clientKey: 'deductionClient' },
];

const Title = styled.div`
	font-size: 20px;
	font-weight: 700;
	color: #1a1a1a;
`;

const Tabs = styled.div`
	display: inline-flex;
	border: 1px solid #d9d9d9;
	border-radius: 6px;
	overflow: hidden;
	margin: 16px 0 20px;
`;

const Tab = styled.button`
	border: none;
	padding: 8px 20px;
	font-size: 14px;
	font-weight: 600;
	cursor: pointer;
	background: ${(p) => (p.active ? '#1e293b' : '#fff')};
	color: ${(p) => (p.active ? '#fff' : '#1f2933')};
`;

const MetaBox = styled.div`
	display: flex;
	gap: 24px;
	align-items: center;
	border: 1px solid #e5e7eb;
	border-radius: 8px;
	padding: 14px 16px;
	margin-bottom: 18px;

	.field {
		display: flex;
		align-items: center;
		gap: 10px;
		flex: 1;
	}
	.label {
		font-size: 13px;
		color: #64748b;
		white-space: nowrap;
	}
`;

const SectionHeading = styled.div`
	font-size: 16px;
	font-weight: 700;
	color: #1a1a1a;
	margin-bottom: 12px;
`;

const InfoBanner = styled.div`
	display: flex;
	align-items: center;
	gap: 8px;
	background: #eff6ff;
	border: 1px solid #bfdbfe;
	border-radius: 6px;
	padding: 10px 14px;
	margin-bottom: 12px;
	font-size: 12px;
	color: #1e40af;
`;

const ChargesTable = styled.table`
	width: 100%;
	border-collapse: collapse;
	border: 1px solid #e5e7eb;
	border-radius: 8px;
	overflow: hidden;

	thead th {
		background: #0f1f33;
		color: #fff;
		font-size: 11px;
		font-weight: 600;
		letter-spacing: 0.3px;
		padding: 12px 16px;
		text-align: center;
	}
	thead th:first-child {
		text-align: left;
	}
	tbody td {
		padding: 8px 16px;
		border-top: 1px solid #eef1f5;
		font-size: 13px;
		color: #1f2933;
		text-align: center;
		vertical-align: middle;
	}
	tbody td.label {
		text-align: left;
		text-transform: uppercase;
		font-size: 12px;
		color: #475569;
	}
	tbody tr.total td {
		background: #f8fafc;
		font-weight: 700;
		color: #15233a;
	}
	.cell-input {
		width: 90px;
		margin: 0 auto;
	}
`;

const UploadBlock = styled.div`
	margin-top: 16px;

	.label {
		display: block;
		font-size: 13px;
		color: #64748b;
		margin-bottom: 8px;
	}
`;

const TotalsRow = styled.div`
	display: flex;
	gap: 24px;
	border: 1px solid #e5e7eb;
	border-radius: 8px;
	padding: 14px 16px;
	margin-top: 18px;

	.field {
		display: flex;
		align-items: center;
		gap: 10px;
		flex: 1;
	}
	.label {
		font-size: 13px;
		color: #64748b;
		white-space: nowrap;
	}
`;

const Footer = styled.div`
	display: flex;
	justify-content: center;
	gap: 16px;
	margin-top: 24px;
`;

const CancelBtn = styled(Button)`
	color: #ef4444;
	border-color: #ef4444;
	border-radius: 6px;
	min-width: 120px;
	height: 40px;
`;

const PrimaryBtn = styled(Button)`
	background: #143e60;
	border-color: #143e60;
	color: #fff;
	border-radius: 6px;
	min-width: 120px;
	height: 40px;
	&:hover,
	&:focus {
		background: #1a4d7a !important;
		border-color: #1a4d7a !important;
		color: #fff !important;
	}
`;

// Quantity already recorded against each rate line by an earlier close submission.
// trip_info.order_quantities is what a close writes and what the Expenses tab Per Order
// table reads; order_line_breakdown is the frozen copy of the same numbers. Matched by
// rate_id rather than by position, since the rate lines are resolved live and their
// order is not guaranteed to line up. Takes the fields rather than the whole trip so
// callers can list them as effect dependencies: the trip object is rebuilt by the
// parent on every render, the nested fields are not.
const recordedQuantitiesByRateId = (orderQuantities, orderLineBreakdown) => {
	const rows = [
		...(Array.isArray(orderQuantities) ? orderQuantities : []),
		...(Array.isArray(orderLineBreakdown) ? orderLineBreakdown : []),
	];
	return rows.reduce((acc, row) => {
		if (!row?.rate_id || acc[row.rate_id] !== undefined) return acc;
		const qty = Number(row.quantity);
		if (!Number.isFinite(qty) || qty <= 0) return acc;
		return { ...acc, [row.rate_id]: String(qty) };
	}, {});
};

// Supporting-evidence keys uploaded by an earlier close submission.
const recordedPerOrderProofKeys = (proofKeys, proofUrls) => {
	if (Array.isArray(proofKeys) && proofKeys.length) return proofKeys.filter(Boolean);
	if (Array.isArray(proofUrls)) return proofUrls.map((entry) => entry?.key).filter(Boolean);
	return [];
};

const toAmount = (v) => `₹ ${(Number(v) || 0).toLocaleString('en-IN')}`;

// Red asterisk marking a mandatory field.
function Req() {
	return <span style={{ color: '#ef4444' }}>*</span>;
}

function UploadField({
	label,
	value,
	onChange,
	entity,
	multiple = true,
	maxCount,
	required = false,
}) {
	return (
		<UploadBlock>
			<span className="label">
				{label} {required && <Req />}
			</span>
			<UploadWithPreviewMultiple
				value={value}
				onChange={onChange}
				buttonText="Upload"
				accept={['.pdf', '.jpg', '.jpeg', '.png']}
				entity={entity}
				multiple={multiple}
				maxCount={maxCount}
			/>
		</UploadBlock>
	);
}

UploadField.propTypes = {
	label: PropTypes.string.isRequired,
	value: PropTypes.arrayOf(PropTypes.string),
	onChange: PropTypes.func.isRequired,
	entity: PropTypes.string,
	multiple: PropTypes.bool,
	maxCount: PropTypes.number,
	required: PropTypes.bool,
};

export default function EndTripModalV2({
	visible,
	onClose,
	trip,
	perOrderRates,
	tableRefreshRef,
	setpaginateConfig,
}) {
	const [activeTab, setActiveTab] = useState(TABS.PER_ORDER);

	const {
		date,
		setDate,
		endKm,
		setEndKm,
		charges,
		handleChange,
		customerProofS3Keys,
		setCustomerProofS3Keys,
		odometerS3Key,
		setOdometerS3Key,
		isPodRequired,
		podS3Keys,
		setPodS3Keys,
		driverPodPreviewKeys,
		handleSubmit,
		loading,
		canSubmit,
		isPrefilledFromPreviousAttempt,
	} = useEndTripModal(trip, visible, onClose, tableRefreshRef, setpaginateConfig);
	const isIntercity = Boolean(trip?.info?.is_inter_city);
	const customerProofEntity = isIntercity ? 'proof_of_delivery_upload' : 'trip';

	// Per Order tab — rate lines come from the trip's order_rates
	const rateLines = useMemo(
		() => (Array.isArray(perOrderRates) ? perOrderRates : []),
		[perOrderRates]
	);
	const [quantities, setQuantities] = useState(() => rateLines.map(() => ''));
	const [proofKeys, setProofKeys] = useState([]);

	// Restore what an earlier close submission already recorded. Driven by an effect on
	// visible rather than by a useState initializer because TripHeader keeps this modal
	// mounted across opens, so an initializer would only ever run once. The dependencies
	// are the nested fields, never the trip object: TripHeader rebuilds that on every
	// render, which would re-run this and wipe whatever the user had typed.
	const recordedOrderQuantities = trip?.info?.order_quantities;
	const recordedOrderLineBreakdown = trip?.info?.order_line_breakdown;
	const recordedProofKeyList = trip?.info?.per_order_proof_keys;
	const recordedProofUrlList = trip?.per_order_proof_urls;

	useEffect(() => {
		if (!visible) return;

		const quantityByRateId = recordedQuantitiesByRateId(
			recordedOrderQuantities,
			recordedOrderLineBreakdown
		);
		setQuantities(rateLines.map((line) => quantityByRateId[line.rate_id] ?? ''));
		setProofKeys(recordedPerOrderProofKeys(recordedProofKeyList, recordedProofUrlList));
	}, [
		visible,
		rateLines,
		recordedOrderQuantities,
		recordedOrderLineBreakdown,
		recordedProofKeyList,
		recordedProofUrlList,
	]);

	const standardTotals = useMemo(
		() =>
			STANDARD_CHARGE_ROWS.reduce(
				(acc, row) => ({
					client: acc.client + (Number(charges?.[row.clientKey]) || 0),
					vendor: acc.vendor + (Number(charges?.[row.vendorKey]) || 0),
				}),
				{ client: 0, vendor: 0 }
			),
		[charges]
	);

	const perOrderRows = useMemo(
		() =>
			rateLines.map((line, i) => {
				const qty = Number(quantities[i]) || 0;
				return {
					...line,
					qty,
					clientCost: (Number(line.client_rate) || 0) * qty,
					vendorCost: (Number(line.vendor_rate) || 0) * qty,
				};
			}),
		[rateLines, quantities]
	);

	const perOrderTotals = useMemo(
		() =>
			perOrderRows.reduce(
				(acc, r) => ({
					client: acc.client + r.clientCost,
					vendor: acc.vendor + r.vendorCost,
				}),
				{ client: 0, vendor: 0 }
			),
		[perOrderRows]
	);
	const order_quantities = rateLines.map((line, i) => ({
		rate_id: line.rate_id,
		quantity: quantities[i] === '' || quantities[i] == null ? 0 : Number(quantities[i]) || 0,
	}));

	const handleEndTrip = (e) => {
		if (!date) {
			message.error('Please select End Date');
			return;
		}
		if (isPodRequired && !podS3Keys.length) {
			message.error('Please upload POD before ending the trip');
			return;
		}
		handleSubmit(e, { order_quantities, proof_keys: proofKeys });
	};
	const canCloseTrip = canSubmit;

	const renderMeta = () => (
		<MetaBox>
			<div className="field">
				<span className="label">
					End Date <Req /> :
				</span>
				<DatePicker
					showTime
					value={date}
					onChange={setDate}
					placeholder="Select date & time"
					suffixIcon={<ClockCircleOutlined />}
					style={{ flex: 1 }}
				/>
			</div>
			<div className="field" style={{ justifyContent: 'flex-end' }}>
				<span className="label">
					End KM <Req /> :
				</span>
				<Input
					value={endKm}
					onChange={(e) => setEndKm(e.target.value)}
					placeholder="500"
					style={{ width: 180 }}
				/>
			</div>
		</MetaBox>
	);

	return (
		<Modal
			open={visible}
			onCancel={onClose}
			footer={null}
			width={900}
			centered
			closeIcon={<CloseOutlined />}
			title={<Title>Close Trip*</Title>}
			bodyStyle={{ maxHeight: '70vh', overflowY: 'auto' }}
		>
			<Tabs>
				<Tab
					type="button"
					active={activeTab === TABS.PER_ORDER}
					onClick={() => setActiveTab(TABS.PER_ORDER)}
				>
					Per Order
				</Tab>
				<Tab
					type="button"
					active={activeTab === TABS.STANDARD}
					onClick={() => setActiveTab(TABS.STANDARD)}
				>
					Standard
				</Tab>
			</Tabs>

			{isPrefilledFromPreviousAttempt && (
				<InfoBanner>
					<InfoCircleOutlined />
					This trip was already submitted for closure. The details below are the
					previously entered values — review and edit them before closing.
				</InfoBanner>
			)}

			{renderMeta()}

			{activeTab === TABS.STANDARD ? (
				<>
					<SectionHeading>Additional Charges</SectionHeading>
					<ChargesTable>
						<thead>
							<tr>
								<th>RATE ID</th>
								<th>CLIENT COST (₹)</th>
								<th>VENDOR COST</th>
							</tr>
						</thead>
						<tbody>
							{STANDARD_CHARGE_ROWS.map((row) => (
								<tr key={row.label}>
									<td className="label">{row.label}</td>
									<td>
										<Input
											className="cell-input"
											value={charges?.[row.clientKey] ?? ''}
											placeholder="0"
											onChange={(e) =>
												handleChange(row.clientKey, e.target.value)
											}
										/>
									</td>
									<td>
										<Input
											className="cell-input"
											value={charges?.[row.vendorKey] ?? ''}
											placeholder="0"
											onChange={(e) =>
												handleChange(row.vendorKey, e.target.value)
											}
										/>
									</td>
								</tr>
							))}
							<tr className="total">
								<td className="label">TOTAL :</td>
								<td>{standardTotals.client.toLocaleString('en-IN')}</td>
								<td>{standardTotals.vendor.toLocaleString('en-IN')}</td>
							</tr>
						</tbody>
					</ChargesTable>

					<UploadField
						label="Additional Charges Customer Proof"
						value={customerProofS3Keys}
						onChange={setCustomerProofS3Keys}
						entity={customerProofEntity}
						multiple
					/>
					<UploadField
						label="Upload odometer End (Optional)"
						value={odometerS3Key ? [odometerS3Key] : []}
						onChange={(files) => setOdometerS3Key(files?.[0] || '')}
						entity="trip"
						multiple={false}
						maxCount={1}
					/>

					{isPodRequired && (
						<>
							<UploadField
								label="Upload POD"
								value={podS3Keys}
								onChange={setPodS3Keys}
								entity="proof_of_delivery_upload"
								multiple
								required
							/>
							{driverPodPreviewKeys && driverPodPreviewKeys.length > 0 && (
								<UploadBlock>
									<span className="label">Driver Uploaded (Read Only)</span>
									<UploadWithPreviewMultiple
										value={driverPodPreviewKeys}
										readonly
										entity="proof_of_delivery_upload"
										multiple
									/>
								</UploadBlock>
							)}
						</>
					)}
				</>
			) : (
				<>
					<SectionHeading>Order Quantity Entry</SectionHeading>
					<InfoBanner>
						<InfoCircleOutlined />
						Enter quantity delivered against each Rate ID. Costs auto-calculated from
						the linked client and vendor contracts.
					</InfoBanner>
					<ChargesTable>
						<thead>
							<tr>
								<th>RATE ID</th>
								<th>VENDOR RATE</th>
								<th>QUANTITY</th>
								<th>VENDOR COST</th>
							</tr>
						</thead>
						<tbody>
							{perOrderRows.map((row, index) => (
								// eslint-disable-next-line react/no-array-index-key
								<tr key={`${row.rate_id}-${index}`}>
									<td className="label">{row.rate_id}</td>
									<td>{row.vendor_rate}</td>
									<td>
										<Input
											className="cell-input"
											value={quantities[index]}
											placeholder="Qty"
											onChange={(e) => {
												const { value } = e.target;
												if (value !== '' && !/^\d+$/.test(value)) {
													return;
												}
												setQuantities((prev) =>
													prev.map((q, i) => (i === index ? value : q))
												);
											}}
										/>
									</td>
									<td>{row.vendorCost.toLocaleString('en-IN')}</td>
								</tr>
							))}
							<tr className="total">
								<td colSpan={3} style={{ textAlign: 'right' }}>
									TOTAL :
								</td>
								<td>{perOrderTotals.vendor.toLocaleString('en-IN')}</td>
							</tr>
						</tbody>
					</ChargesTable>

					<TotalsRow>
						<div className="field">
							<span className="label">Total Vendor Trip Cost :</span>
							<Input value={toAmount(perOrderTotals.vendor)} disabled />
						</div>
					</TotalsRow>

					<UploadField
						label="Upload Supporting Evidence"
						value={proofKeys}
						onChange={setProofKeys}
						entity="trip"
						multiple
					/>
				</>
			)}

			<Footer>
				<CancelBtn onClick={onClose}>Cancel</CancelBtn>
				<PrimaryBtn
					onClick={handleEndTrip}
					loading={loading}
					disabled={loading || !canCloseTrip}
				>
					Close Trip
				</PrimaryBtn>
			</Footer>
		</Modal>
	);
}

EndTripModalV2.propTypes = {
	visible: PropTypes.bool.isRequired,
	onClose: PropTypes.func.isRequired,
	trip: PropTypes.object,
	perOrderRates: PropTypes.arrayOf(PropTypes.object),
	tableRefreshRef: PropTypes.object,
	setpaginateConfig: PropTypes.func,
};
